import { IconX } from '@lib/icons'
import cx from 'classnames'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { PASSWORD_WITHOUT_LIMIT_REGEX } from '@/constants/constants'
import { Rule } from './components/Rule'
import { CHARACTERS, RULES } from './constants'
import css from './PasswordInput.module.scss'

type Props = {
  name: string
  id: string
  error?: string | null
  placeholder?: string
  label?: string

  onChange: (value: string) => void
}

const PasswordInput: React.FC<Props> = ({
  name,
  placeholder,
  id,
  label,
  error,
  onChange,
}) => {
  const [showRules, setShowRules] = useState(false)
  const [rules, setRules] = useState(RULES)
  const [password, setPassword] = useState('')
  const [canClear, setCanClear] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (password) {
      setCanClear(true)
    } else {
      setCanClear(false)
    }
  }, [password])

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value.length === 0 || value.length < CHARACTERS) {
      setShowRules(true)
    }
  }

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target

    if (value.length === 0) {
      setShowRules(false)
    }
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    onChange(value)
    setPassword(value)

    setRules([
      {
        ...rules[0],
        isValid: PASSWORD_WITHOUT_LIMIT_REGEX.exec(value) !== null,
      },
      {
        ...rules[1],
        isValid: value.length >= 8,
      },
    ])

    if (value.length < CHARACTERS) {
      setShowRules(true)
    }
  }

  const handleOnClear = () => {
    setPassword('')

    inputRef.current?.focus()
  }

  return (
    <div>
      <div>
        {label && (
          <label className={css.label} htmlFor={id}>
            {label}
          </label>
        )}

        <div className={css.inputWrapper}>
          <input
            ref={inputRef}
            type="password"
            className={css.input}
            name={name}
            placeholder={placeholder}
            id={id}
            value={password}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleOnChange}
          />
          <button
            type="button"
            className={cx(css.clear, {
              [css.show]: canClear,
            })}
            onClick={handleOnClear}
          >
            <IconX size={16} />
          </button>
        </div>
      </div>

      {error && <div className={css.error}>{error}</div>}

      {showRules && (
        <div className={css.rules}>
          {rules.map((rule, key) => (
            <Rule {...rule} key={key} />
          ))}
        </div>
      )}
    </div>
  )
}

export default PasswordInput
